import {
  Box,
  Button,
  IconButton,
  ListSubheader,
  TextField,
  MenuItem,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';

import type { FacetGroup } from '@src/facets';
import FacetAutocomplete from './FacetAutocomplete';
import {
  CONDITION_FIELD_GROUPS,
  CONDITION_OPERATOR_OPTIONS,
  NUMERIC_OPERATOR_OPTIONS,
  JSON_OPERATOR_OPTIONS,
  generateId,
  isFamilyField,
  isJsonField,
  isNumericField,
  type CatalogRuleCondition,
  type KeyValuePair,
  type LogicalOperator,
} from './types';

interface ConditionRowProps {
  condition: CatalogRuleCondition;
  isFirst: boolean;
  facetGroups: FacetGroup[];
  onChange: (id: string, updates: Partial<CatalogRuleCondition>) => void;
  onDelete: (id: string) => void;
  onSiblingOperatorChange: (newOperator: LogicalOperator) => void;
}

const ConditionRow = ({ condition, isFirst, facetGroups, onChange, onDelete, onSiblingOperatorChange }: ConditionRowProps) => {
  const jsonMode = isJsonField(condition.field);
  const familyMode = isFamilyField(condition.field);
  const numericMode = isNumericField(condition.field);

  const getOperatorOptions = () => {
    if (jsonMode) return JSON_OPERATOR_OPTIONS;
    if (numericMode) return NUMERIC_OPERATOR_OPTIONS;
    return CONDITION_OPERATOR_OPTIONS;
  };
  const operatorOptions = getOperatorOptions();
  const needsValue = !['isEmpty', 'isNotEmpty'].includes(condition.operator);

  const getFieldType = (field: string) => {
    if (isJsonField(field)) return 'json';
    if (isNumericField(field)) return 'numeric';
    return 'string';
  };

  const handleFieldChange = (field: string) => {
    const prevType = getFieldType(condition.field);
    const nextType = getFieldType(field);
    const typeChanged = prevType !== nextType;

    onChange(condition.id, {
      field,
      ...(typeChanged ? { operator: '' } : {}),
      ...(nextType === 'json' && prevType !== 'json' ? { value: '', pairs: [{ id: generateId(), key: '', value: '', values: [] }] } : {}),
      ...(nextType !== 'json' && prevType === 'json' ? { value: '', pairs: [] } : {}),
    });
  };

  const handlePairChange = (pairId: string, updates: Partial<KeyValuePair>) => {
    onChange(condition.id, {
      pairs: condition.pairs.map(p =>
        p.id === pairId ? { ...p, ...updates } : p,),
    });
  };

  const handleAddPair = () => {
    onChange(condition.id, {
      pairs: [...condition.pairs, { id: generateId(), key: '', value: '', values: [] }],
    });
  };

  const handleRemovePair = (pairId: string) => {
    onChange(condition.id, {
      pairs: condition.pairs.filter(p => p.id !== pairId),
    });
  };

  const getAvailableTagChoices = (currentPairId: string): string[] => {
    const usedTags = new Set(
      condition.pairs.filter(p => p.id !== currentPairId).map(p => p.key).filter(Boolean),
    );
    return facetGroups.map(fg => fg.tag).filter(tag => !usedTags.has(tag));
  };

  const getValueChoices = (tag: string): string[] => {
    const facetGroup = facetGroups.find(fg => fg.tag === tag);
    return facetGroup?.values.map(v => v.name) ?? [];
  };

  return (
    <Box mb={1.5}>
      <Box display="flex" alignItems="flex-start" gap={1.5}>
        {isFirst ? (
          <Typography variant="body2" fontWeight={600} sx={{ minWidth: 80, mt: 1, color: 'text.primary', whiteSpace: 'nowrap' }}>
            WHEN
          </Typography>
        ) : (
          <TextField
            select
            size="small"
            value={condition.logicalOperator}
            onChange={e => onSiblingOperatorChange(e.target.value as LogicalOperator)}
            sx={{ minWidth: 80, maxWidth: 80 }}
            InputProps={{ sx: { bgcolor: 'background.paper' } }}
          >
            <MenuItem value="and">and</MenuItem>
            <MenuItem value="or">or</MenuItem>
          </TextField>
        )}

        <TextField
          select
          size="small"
          value={condition.field}
          onChange={e => handleFieldChange(e.target.value)}
          sx={{ width: 180, flexShrink: 0 }}
          SelectProps={{ displayEmpty: true }}
          InputProps={{ sx: { bgcolor: 'background.paper' } }}
        >
          <MenuItem value="" disabled>Select a Field</MenuItem>
          {CONDITION_FIELD_GROUPS.flatMap(group => [
            <ListSubheader key={group.group} sx={{ fontWeight: 700, color: 'text.primary' }}>{group.group}</ListSubheader>,
            ...group.options.map(opt => (
              <MenuItem key={opt.value} value={opt.value} sx={{ pl: 3 }}>{opt.label}</MenuItem>
            )),
          ])}
        </TextField>

        <TextField
          select
          size="small"
          value={condition.operator}
          onChange={e => onChange(condition.id, { operator: e.target.value })}
          sx={{ minWidth: 160 }}
          SelectProps={{ displayEmpty: true }}
          InputProps={{ sx: { bgcolor: 'background.paper' } }}
        >
          <MenuItem value="" disabled>Select</MenuItem>
          {operatorOptions.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>

        {/* Standard value input for non-JSON fields */}
        {!jsonMode && needsValue && (
          <TextField
            size="small"
            placeholder="Select a Value"
            value={condition.value}
            onChange={e => onChange(condition.id, { value: e.target.value })}
            sx={{ flex: 1, minWidth: 180 }}
            InputProps={{ sx: { bgcolor: 'background.paper' } }}
          />
        )}

        {/* Family field: facet tag + multi-select values */}
        {familyMode && needsValue && (
          <Box sx={{ flex: 1 }}>
            {condition.pairs.map((pair, i) => (
              <Box key={pair.id} display="flex" alignItems="flex-start" gap={1} mt={i > 0 ? 1 : 0}>
                <FacetAutocomplete
                  options={getAvailableTagChoices(pair.id)}
                  value={pair.key || null}
                  onChange={value => handlePairChange(pair.id, { key: value, values: [] })}
                  placeholder="Facet Tag"
                  matchLabel="Tag Matches"
                  sx={{ minWidth: 150 }}
                />
                <FacetAutocomplete
                  multiple
                  options={getValueChoices(pair.key)}
                  value={pair.values}
                  onChange={values => handlePairChange(pair.id, { values })}
                  placeholder={pair.key ? 'Select values' : 'Select a tag first'}
                  matchLabel="Value Matches"
                  disabled={!pair.key}
                  sx={{ flex: 1, minWidth: 200 }}
                />
                <IconButton size="small" onClick={() => handleRemovePair(pair.id)} sx={{ mt: 0.5, color: 'text.secondary' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddPair}
              sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
            >
              Add Facet
            </Button>
          </Box>
        )}

        {/* Other JSON fields: plain key/value text inputs */}
        {jsonMode && !familyMode && needsValue && (
          <Box sx={{ flex: 1 }}>
            {condition.pairs.map((pair, i) => (
              <Box key={pair.id} display="flex" alignItems="center" gap={1} mt={i > 0 ? 1 : 0}>
                <TextField
                  size="small"
                  placeholder="Key"
                  value={pair.key}
                  onChange={e => handlePairChange(pair.id, { key: e.target.value })}
                  sx={{ minWidth: 100 }}
                  InputProps={{ sx: { bgcolor: 'background.paper' } }}
                />
                <TextField
                  size="small"
                  placeholder="Value"
                  value={pair.value}
                  onChange={e => handlePairChange(pair.id, { value: e.target.value })}
                  sx={{ minWidth: 120 }}
                  InputProps={{ sx: { bgcolor: 'background.paper' } }}
                />
                <IconButton size="small" onClick={() => handleRemovePair(pair.id)} sx={{ color: 'text.secondary' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddPair}
              sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
            >
              Add Pair
            </Button>
          </Box>
        )}

        <IconButton size="small" onClick={() => onDelete(condition.id)} sx={{ mt: 0.25, color: 'text.secondary' }}>
          <DeleteOutlineIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ConditionRow;
